<template>
  <div class="container-fluid p-0">
    <!-- Loading Spinner -->
    <div v-if="loading" class="text-center py-5">
      <Spinner spinner-class="mb-1" />
      <p class="text-muted mt-2">Loading stock...</p>
    </div>

    <!-- Stock Content -->
    <div v-if="!loading" class="stock-content fade-in">
      <!-- Products Count & Search -->
      <div class="d-flex flex-wrap align-items-center justify-content-between bg-white shadow-sm rounded p-3 mb-3">
        <div class="d-flex align-items-center mb-2 mb-sm-0">
          <h5 class="badge bg-primary rounded-pill me-2">{{ filteredProducts.length }}</h5>
          <h5 class="text-muted mt-1">{{ store.name }} Products</h5>
        </div>
        <div class="d-flex flex-wrap align-items-center gap-2">
          <UInput v-model="dataTableSearch" size="md" placeholder="Search sku, code, name" class="mb-0 w-auto" />

          <!-- Product Status Filter -->
          <select v-model="statusFilter" class="form-select w-auto" data="">
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="discontinued">Discontinued</option>
            <option value="inactive">Inactive</option>
          </select>

          <!-- Reorder Status Filter -->
          <select v-model="reorderFilter" class="form-select w-auto" data="">
            <option value="">All Status</option>
            <option value="reorder">Need Reorder</option>
            <option value="good">Good</option>
          </select>

          <!-- Price Sort -->
          <select v-model="priceSort" class="form-select w-auto" data="">
            <option value="">Price — None</option>
            <option value="asc">Price &uarr; Low to High</option>
            <option value="desc">Price &darr; High to Low</option>
          </select>

          <!-- Expiry Date Filter -->
          <select v-model="expiryFilter" class="form-select w-auto" data="">
            <option value="">Expiry — All</option>
            <option value="1">Expiring in 1 month</option>
            <option value="2">Expiring in 2 months</option>
            <option value="3">Expiring in 3 months</option>
          </select>

          <!-- Extra -->
          <slot name="header-actions"></slot>
        </div>
      </div>

      <!-- Products Table -->
      <div class="card border-0 shadow-sm mb-4">
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead>
            <tr>
              <th>SKU</th>
              <th>CODE</th>
              <th>NAME</th>
              <th class="text-end">BUYING PRICE</th>
              <th class="text-end">SELLING PRICE</th>
              <th v-if="!hideStock">STOCK</th>
              <th v-if="!hideStock">REORDER</th>
              <th v-if="!hideStock">R. STATUS</th>
              <th>STATUS</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="(storeProduct, index) in paginatedProducts"
                :key="storeProduct.id"
                class="product-row clickable-row"
                :class="{'bg-light': index % 2 === 0, 'marked-row': isProductMarked(storeProduct.product_id)}"
                @click="rowClick(storeProduct)"
            >
              <td>{{ storeProduct.product.sku }}</td>
              <td>{{ storeProduct.product.code }}</td>
              <td>{{ storeProduct.product.name }}</td>
              <td class="text-end">{{ formatNumber(storeProduct.product.buy) }}</td>
              <td class="text-end">{{ formatNumber(storeProduct.price) }}</td>
              <td v-if="!hideStock">{{ storeProduct.stock }} {{ storeProduct.product.unit.name }}</td>
              <td v-if="!hideStock">{{ storeProduct.reorder }} {{ storeProduct.product.unit.name }}</td>
              <td v-if="!hideStock">
                <Badge pill :type="getPillByReorder(storeProduct.reorder < storeProduct.stock ? 'good' : 'reorder')">
                  {{ storeProduct.reorder < storeProduct.stock ? 'good' : 'reorder' }}
                </Badge>
              </td>
              <td>
                <Badge pill :type="getPillByStatus(storeProduct.product.status)">
                  {{ storeProduct.product.status }}
                </Badge>
              </td>
              <td class="text-end">
                <!-- Action buttons injected via slot -->
                <slot name="actions" :storeProduct="storeProduct"></slot>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <hr class="m-0">

        <!-- Pagination Controls -->
        <div v-if="filteredProducts.length > 0" class="bg-white">
          <div class="d-flex flex-wrap align-items-end justify-content-between mx-4 mb-3 mt-3">
            <div class="row me-sm-4 mb-sm-0 mb-3">
              <div class="col-auto"><span class="mb-2 small">Rows per page:</span></div>
              <div class="col-auto"><select class="form-select" v-model.number="dataTablePerPage" data="">
                <option v-for="item in [5,10,25,50,100,250,500]" :key="item" :value="item">{{item}}</option>
              </select></div>
            </div>
            <div>
              <Pagination
                  v-model="dataTablePage"
                  :pages="totalPages"
                  :threshold="2"
              >
                <template #previous>Prev</template>
                <template #next>Next</template>
              </Pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {decrypt} from "@/utils/crypto";
import formatNumber from "@/utils/formatNumber";
import storeStockService from "@/api/storeStockService";
import $ from 'jquery';
import notify from "@/utils/notify";

export default {
  name: "StoreProducts",

  props: {
    perPage: {
      type: Number,
      default: 10
    },
    // Array of products or product IDs that should be highlighted in the table
    markedProducts: {
      type: Array,
      default: () => []
    },
    // Hide stock-related columns (used in stock take)
    hideStock: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      store: null,
      loading: true,
      dataTablePage: 1,
      dataTableSearch: '',
      dataTablePerPage: this.perPage,
      dataTable: [],
      statusFilter: '',
      reorderFilter: '',
      priceSort: '',
      expiryFilter: '',
    }
  },

  methods: {
    formatNumber,
    async getStoreStock() {
      this.dataTable = await storeStockService.getStoreStock(this.store.id)
      this.loading = false
    },
    rowClick(storeProduct) {
      if (this.isProductMarked(storeProduct.product_id)) {
        notify.alert.info('Product already added', 2000)
      } else {
        this.$emit('row-click', storeProduct)
      }
    },
    getPillByReorder(cellData) {
      switch (cellData) {
        case 'reorder':
          return 'danger';
        case 'good':
          return 'success';
      }
    },
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'inactive':
          return 'secondary';
        case 'active':
          return 'success';
        case 'discontinued':
          return 'warning';
        default:
          return 'info';
      }
    },
    isProductMarked(productId) {
      if (!this.markedProducts) return false;
      
      // Handle both array format and paginated object format {data: [], total: ...}
      const products = Array.isArray(this.markedProducts) 
        ? this.markedProducts 
        : (this.markedProducts.data || []);
      
      if (products.length === 0) return false;

      return products.some(item => {
        return item.product_id === productId;
      });
    },
    /** Apply .marked-row class to all td inside already marked tr */
    applyRowHighlight() {
      // Ensure DOM updated
      this.$nextTick(() => {
        $(this.$el)
          .find('tr.marked-row')
          .each(function () {
            $(this).find('td').addClass('marked-row');
          });
      });
    },
  },

  mounted() {
    this.applyRowHighlight();
  },

  updated() {
    this.applyRowHighlight();
  },

  async created() {
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    await this.getStoreStock()
  },

  computed: {
    filteredProducts(){
      // text search
      let products = this.dataTable;
      if (this.dataTableSearch) {
        const term = this.dataTableSearch.toLowerCase().trim();
        const terms = term.split(/\s+/).filter(Boolean);

        products = products.filter(p => {
          const searchable = [p.product.code, p.product.name, p.product.sku]
            .filter(Boolean)
            .map(s => s.toLowerCase());

          if (terms.length > 1) {
            // every term must appear in at least one searchable field
            return terms.every(t => searchable.some(field => field.includes(t)));
          }
          // single term behaviour
          return searchable.some(field => field.includes(term));
        });
      }

      // product status filter
      if (this.statusFilter) {
        products = products.filter(p => {
          return p.product && p.product.status === this.statusFilter;
        });
      }

      // reorder status filter
      if (this.reorderFilter) {
        products = products.filter(p => {
          const status = p.reorder < p.stock ? 'good' : 'reorder';
          return status === this.reorderFilter;
        });
      }

      // expiry date filter
      if (this.expiryFilter) {
        const today = new Date();
        const monthsAhead = parseInt(this.expiryFilter);
        const futureDate = new Date(today);
        futureDate.setMonth(futureDate.getMonth() + monthsAhead);

        products = products.filter(p => {
          if (!p.expiry_date) return false;
          const expiryDate = new Date(p.expiry_date);
          return expiryDate >= today && expiryDate <= futureDate;
        });
      }

      // price sort
      if (this.priceSort === 'asc') {
        products = [...products].sort((a,b)=>a.price - b.price);
      } else if (this.priceSort === 'desc') {
        products = [...products].sort((a,b)=>b.price - a.price);
      }

      return products;
    },
    paginatedProducts(){
      const start = (this.dataTablePage - 1) * this.dataTablePerPage;
      return this.filteredProducts.slice(start, start + this.dataTablePerPage);
    },
    totalPages(){
      return Math.ceil(this.filteredProducts.length / this.dataTablePerPage) || 1;
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
    },
    dataTableSearch(){
      this.dataTablePage = 1;
    },
    reorderFilter(){
      this.dataTablePage = 1;
    },
    priceSort(){
      this.dataTablePage = 1;
    },
    expiryFilter(){
      this.dataTablePage = 1;
    }
  },
}
</script>

<style scoped>
/* Stock page styles */
.stock-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  font-size: 0.9rem;
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #dde5ee;
  color: #4b5563;
}

.product-row {
  transition: all 0.2s ease;
}

.product-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.clickable-row {
  cursor: pointer;
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.marked-row {
  background-color: #b4d6ff !important;
}
</style>
